import { useState } from 'react';
import { RequirePermission } from '../components/NoAccess';
import { Link, useNavigate } from 'react-router-dom';
import type {
  ComplianceSummary, DueBucket, DueList as Due, DueRow,
} from '@dru/types';
import { DUE_BUCKET_LABEL } from '@dru/types';
import {
  Alert, Button, DataTable, ErrorState, FilterBar, IconAction, RowActions, SearchInput,
  Select, StatusBadge, type Tone, cn, useToast,
} from '@dru/ui';
import { CalendarPlus, Eye } from 'lucide-react';
import { api } from '../../../lib/api';
import { formatDate } from '../../../lib/format';
import { useQuery } from '../../../lib/useQuery';
import { useAuth } from '../../../lib/auth';
import { PageHeader, holds } from '../../../layouts/ConsoleShell';
import { ComplianceViews, CoverageFigures, usePagePlantFilter } from './Compliance';
import { ArrangeDialog } from '../components/ArrangeDialog';

/**
 * What to do this week.
 *
 * Distinct from the compliance screen, which answers "who is not covered".
 * This one is a worklist ordered by time, and the buckets come from the server
 * so the count on the tab and the rows underneath it cannot disagree — a
 * client computing "due this week" from one page of results reports a smaller
 * number than the truth, and the number is what somebody plans against.
 *
 * A worker appears in exactly one bucket, the most urgent that applies, so the
 * buckets sum to the total and nobody is actioned twice.
 *
 * Workers with no certificate at all are not renewals and so never appear
 * here. The coverage counts at the top still name them (never examined, not
 * cleared), because a due list that reads "nothing due" while eighteen people
 * have never been examined is the most dangerous empty state on the product.
 */

const ORDER: DueBucket[] = [
  'OVERDUE', 'DUE_TODAY', 'DUE_THIS_WEEK', 'DUE_THIS_MONTH', 'EXPIRING_SOON',
];

/** "Expired", as on the overview, rather than the shared "Overdue". */
const bucketLabel = (b: DueBucket) => (b === 'OVERDUE' ? 'Expired' : DUE_BUCKET_LABEL[b]);

const bucketTone = (b: DueBucket): Tone =>
  b === 'OVERDUE' ? 'overdue'
  : b === 'DUE_TODAY' ? 'overdue'
  : b === 'DUE_THIS_WEEK' ? 'dueSoon'
  : b === 'DUE_THIS_MONTH' ? 'dueSoon'
  : 'pending';

function DueListPage() {
  const navigate = useNavigate();
  const { permissions } = useAuth();
  const { toast } = useToast();
  const [bucket, setBucket] = useState<DueBucket | ''>('');
  const [plantId, setPlantId] = useState('');
  const [withinDays, setWithinDays] = useState('90');
  const [search, setSearch] = useState('');
  const [arranging, setArranging] = useState<DueRow | null>(null);

  const plant = usePagePlantFilter();
  const canArrange = holds(permissions, 'examination.create');

  const query = new URLSearchParams({ withinDays, limit: '300' });
  if (plantId) query.set('plantId', plantId);

  const due = useQuery<Due>(
    () => api.get<Due>(`/compliance/due?${query.toString()}`),
    [plantId, withinDays],
  );

  // The same six counts as the overview, over the same look-ahead, so "Due
  // soon" here and the rows below describe one window.
  const summary = useQuery<ComplianceSummary>(
    () => api.get(`/compliance/summary?dueWithinDays=${withinDays}${plantId ? `&plantId=${plantId}` : ''}`),
    [plantId, withinDays],
  );
  const never = summary.data?.neverExamined ?? 0;
  const notCleared = summary.data?.notCleared ?? 0;

  const all = due.data?.rows ?? [];
  const needle = search.trim().toLowerCase();
  const rows = all.filter((r) => (!bucket || r.bucket === bucket)
    && (!needle || r.fullName.toLowerCase().includes(needle) || r.employeeCode.toLowerCase().includes(needle)));
  const counts = due.data?.buckets;
  const ahead = withinDays === '365' ? 'within a year' : `within ${withinDays} days`;

  return (
    <div className="space-y-4">
      <PageHeader
        className="!mb-0"
        title="Compliance"
        hint="Certificate renewals, soonest first. An employee appears once, in the most urgent window that applies."
      />

      <ComplianceViews active="due" />

      <CoverageFigures
        summary={summary.data} loading={summary.loading} dueLabel={ahead}
        // Renewals stay on this view: Expired picks its window, Due soon shows
        // every renewal in the look-ahead. The rest live on the overview.
        onPick={(to) => {
          if (to === 'OVERDUE') { setBucket('OVERDUE'); return; }
          if (to === 'DUE_SOON') { setBucket(''); return; }
          navigate(to === 'employees' ? '/hr/employees'
            : to === 'covered' ? '/hr/certificates' : `/hr/compliance?state=${to}`);
        }}
      />

      {due.error ? <ErrorState detail={due.error} onRetry={due.reload} /> : (
        <>
          {(never > 0 || notCleared > 0) && (
            <p className="text-small text-neutral-600">
              {never + notCleared} {never + notCleared === 1 ? 'employee has' : 'employees have'} no certificate to renew
              {' '}({never} never examined{notCleared ? `, ${notCleared} not cleared` : ''}), so they are not on this list.{' '}
              <Link to="/hr/compliance" className="font-medium text-primary-700 hover:underline">See them in the overview →</Link>
            </p>
          )}

          {due.data?.truncated && (
            <Alert tone="warning" title="More than this list can show">
              The window counts below describe the {all.length} soonest renewals, not your
              whole company. Narrow the look-ahead{plant.filter ? ' or filter by plant' : ''} to see a complete picture.
            </Alert>
          )}

          {/* Windows as toggles with their counts: the count is the point.
              One line on a phone, scrolled sideways. */}
          <div className="flex flex-wrap gap-1.5 max-md:flex-nowrap max-md:overflow-x-auto max-md:pb-1 max-md:[&>*]:shrink-0" role="group" aria-label="Due window">
            <BucketTab label="All" count={all.length} active={bucket === ''} onClick={() => setBucket('')} />
            {ORDER.map((b) => (
              <BucketTab
                key={b}
                label={BUCKET_CHIP[b]}
                count={counts?.[b] ?? 0}
                tone={bucketTone(b)}
                active={bucket === b}
                onClick={() => setBucket(b)}
              />
            ))}
          </div>

          <FilterBar
            filters={plant.filter
              ? [{ key: 'plant', label: 'Plant', allLabel: 'All plants', options: plant.options }]
              : []}
            more={(
              <label className="inline-flex items-center gap-2 text-caption text-neutral-600">
                Look ahead
                <Select
                  value={withinDays}
                  onChange={(e) => setWithinDays(e.target.value)}
                  className="h-10 w-auto py-0 text-small"
                >
                  <option value="30">30 days</option>
                  <option value="60">60 days</option>
                  <option value="90">90 days</option>
                  <option value="180">180 days</option>
                  <option value="365">A year</option>
                </Select>
              </label>
            )}
            moreActive={withinDays !== '90' ? 1 : 0}
            value={{ plant: plantId || undefined }}
            onChange={(v) => setPlantId(v.plant ?? '')}
          >
            <SearchInput
              value={search}
              onValueChange={setSearch}
              placeholder="Search name or employee ID"
              aria-label="Search by name or employee ID"
              className="w-full sm:w-64"
            />
          </FilterBar>

          <DataTable<DueRow>
            rows={rows}
            loading={due.loading}
            error={due.error}
            onRetry={due.reload}
            rowKey={(r) => r.employeeId}
            onRowClick={(r) => navigate(`/hr/employees/${r.employeeId}`)}
            empty={{
              title: needle
                ? 'Nobody matches this search'
                : bucket
                  ? `Nothing ${bucketLabel(bucket as DueBucket).toLowerCase()}`
                  : `No certificate expires ${ahead}`,
              hint: needle
                ? 'Clear the search to see every renewal in this window.'
                : 'Widen the look-ahead to plan further out.',
            }}
            columns={[
              {
                key: 'code', header: 'Employee ID',
                cell: (r) => <span className="whitespace-nowrap font-mono text-small text-neutral-700">{r.employeeCode}</span>,
              },
              {
                key: 'employee', header: 'Name', alwaysVisible: true,
                cell: (r) => (
                  <Link
                    to={`/hr/employees/${r.employeeId}`}
                    onClick={(e) => e.stopPropagation()}
                    className="font-medium text-neutral-900 hover:text-primary-700"
                  >
                    {r.fullName}
                  </Link>
                ),
              },
              ...(plant.column ? [{
                key: 'plant', header: 'Plant', className: 'max-lg:hidden',
                cell: (r: DueRow) => <span className="text-small text-neutral-700">{r.plant ?? '—'}</span>,
              }] : []),
              {
                key: 'status', header: 'Status',
                cell: (r) => <StatusBadge tone={bucketTone(r.bucket)}>{bucketLabel(r.bucket)}</StatusBadge>,
              },
              {
                key: 'due', header: 'Due date',
                cell: (r) => (
                  <div className="whitespace-nowrap">
                    <div className="text-small tabular-nums text-neutral-800">{formatDate(r.dueOn)}</div>
                    <div
                      className={cn(
                        'text-caption tabular-nums',
                        r.daysRemaining < 0 ? 'font-medium text-overdue-600'
                          : r.daysRemaining <= 7 ? 'text-dueSoon-600' : 'text-neutral-500',
                      )}
                    >
                      {r.daysRemaining < 0
                        ? `Expired ${Math.abs(r.daysRemaining)} ${Math.abs(r.daysRemaining) === 1 ? 'day' : 'days'} ago`
                        : r.daysRemaining === 0 ? 'Expires today'
                          : `In ${r.daysRemaining} ${r.daysRemaining === 1 ? 'day' : 'days'}`}
                    </div>
                  </div>
                ),
              },
              {
                key: 'next', header: 'Next action',
                // The one column that stops HR chasing something already booked.
                cell: (r) => (
                  <RowActions className="justify-start">
                    {r.renewalInProgress ? (
                      <StatusBadge tone="info">Renewal booked — with the lab</StatusBadge>
                    ) : canArrange ? (
                      <Button size="sm" icon={<CalendarPlus className="h-4 w-4" />}
                              aria-label={`Schedule renewal for ${r.fullName}`}
                              onClick={(e) => { e.stopPropagation(); setArranging(r); }}>
                        Schedule renewal
                      </Button>
                    ) : (
                      <span className="text-small text-neutral-500">Renewal not booked</span>
                    )}
                    <IconAction label={`View ${r.fullName}`} icon={Eye}
                                onClick={() => navigate(`/hr/employees/${r.employeeId}`)} />
                  </RowActions>
                ),
              },
            ]}
          />
        </>
      )}

      {arranging && (
        <ArrangeDialog
          employee={{ id: arranging.employeeId, fullName: arranging.fullName, employeeCode: arranging.employeeCode }}
          onClose={() => setArranging(null)}
          onDone={() => {
            toast('success', `Renewal checkup arranged for ${arranging.fullName}.`);
            setArranging(null); due.reload(); summary.reload();
          }}
        />
      )}
    </div>
  );
}

/** Short chip labels: the list already says it is about certificates falling due. */
const BUCKET_CHIP: Record<DueBucket, string> = {
  OVERDUE: 'Expired', DUE_TODAY: 'Today', DUE_THIS_WEEK: 'This week',
  DUE_THIS_MONTH: 'This month', EXPIRING_SOON: 'Later',
};

function BucketTab({
  label, count, tone, active, onClick,
}: {
  label: string;
  count: number;
  tone?: Tone;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'flex min-h-8 max-md:min-h-10 items-baseline gap-2 rounded-input border px-3 py-1.5 text-small font-medium transition-colors',
        active
          ? 'border-primary-600 bg-primary-600 text-white'
          : 'border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50',
      )}
    >
      {label}
      <span
        className={cn(
          'tabular-nums',
          active ? 'text-white'
            : count > 0 && tone === 'overdue' ? 'font-semibold text-overdue-600'
            : 'text-neutral-500',
        )}
      >
        {count}
      </span>
    </button>
  );
}

export function DueList() {
  return <RequirePermission need="compliance.read" title="Compliance" what="Certificate renewals"><DueListPage /></RequirePermission>;
}
